<template>
    <div class="operation-log">
        <div class="filter-section">
            <div class="filter-row">
                <div class="filter-item">
                    <span>操作人</span>
                    <a-input placeholder="请输入操作人" style="width: 200px" v-model="filters.operator" allowClear @change="debouncedFilterChange" />
                </div>
                <div class="filter-item">
                    <span>操作模块</span>
                    <a-input placeholder="请输入操作模块" style="width: 200px" v-model="filters.module" allowClear @change="debouncedFilterChange" />
                </div>
                <div class="filter-item">
                    <span>操作名</span>
                    <a-input placeholder="请输入操作名" style="width: 200px" v-model="filters.name" allowClear @change="debouncedFilterChange" />
                </div>
                <div class="filter-item">
                    <span>操作内容</span>
                    <a-input placeholder="请输入操作内容" style="width: 200px" v-model="filters.content" allowClear @change="debouncedFilterChange" />
                </div>
            </div>
            <div class="filter-row">
                <div class="filter-item">
                    <span>操作时间</span>
                    <a-range-picker style="width: 240px" v-model="filters.timeRange" format="YYYY-MM-DD" @change="handleFilterChange" />
                </div>
                <div class="filter-item">
                    <span>业务编码</span>
                    <a-input placeholder="请输入业务编码" style="width: 200px" v-model="filters.businessCode" allowClear @change="debouncedFilterChange" />
                </div>
                <div class="filter-item">
                    <span>IP地址</span>
                    <a-input placeholder="请输入IP地址" style="width: 200px" v-model="filters.ip" allowClear @change="debouncedFilterChange" />
                </div>
                <div class="filter-item button-group">
                    <a-button icon="reload" @click="handleReset">重置</a-button>
                </div>
            </div>
        </div>

        <a-table rowKey="id" :columns="columns" :dataSource="allLogData" :pagination="pagination" :loading="loading" bordered size="middle" :rowClassName="() => 'table-row'" @change="handleTableChange" :scroll="{ x: 1200 }">
            <span slot="operation" slot-scope="text, record">
                <a-button type="link" size="small" class="detail-btn" @click="showDetails(record)">详情</a-button>
            </span>
        </a-table>
        <div class="export-section">
            <a-button type="primary" icon="download" :loading="exportLoading" @click="handleExport">导出日志</a-button>
        </div>
    </div>
</template>

<script>
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import { Modal } from 'ant-design-vue';
import { getOperationLogsWithFullPath, exportOperationLogsWithFullPath } from '@/api/operationLog';
import { getOperationDisplay, parseOperationContent } from './operationMap';

const EXPORT_LIMIT = 10000;

export default {
    name: 'OperationLog',
    data() {
        return {
            filters: {
                operator: '',
                module: '',
                name: '',
                content: '',
                timeRange: [],
                businessCode: '',
                ip: '',
            },
            columns: [
                { title: '日志编号', dataIndex: 'id', key: 'id', width: 80, align: 'center' },
                { title: '操作人', dataIndex: 'operator', key: 'operator', width: 100, align: 'center' },
                { title: '操作模块', dataIndex: 'module', key: 'module', width: 120, align: 'center' },
                { title: '操作名', dataIndex: 'nameDisplay', key: 'nameDisplay', width: 180, align: 'center' },
                {
                    title: '操作内容',
                    dataIndex: 'contentDisplay',
                    key: 'contentDisplay',
                    width: 300,
                    ellipsis: true,
                    customRender: (text) => text || '-',
                },
                {
                    title: '状态',
                    dataIndex: 'status',
                    key: 'status',
                    width: 80,
                    align: 'center',
                    customRender: (text) => text || '-',
                },
                { title: '操作时间', dataIndex: 'time', key: 'time', width: 180, align: 'center' },
                { title: '业务编号', dataIndex: 'businessCode', key: 'businessCode', width: 100, align: 'center' },
                { title: 'IP', dataIndex: 'ip', key: 'ip', width: 140, align: 'center' },
                {
                    title: '操作',
                    key: 'operation',
                    width: 80,
                    align: 'center',
                    fixed: 'right',
                    scopedSlots: { customRender: 'operation' },
                },
            ],
            allLogData: [],
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0,
                showTotal: total => `共 ${total} 条`,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
            },
            loading: false,
            exportLoading: false,
        };
    },
    created() {
        this.debouncedFilterChange = debounce(this.handleFilterChange, 300);
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        getOperationDisplay,
        parseOperationContent,
        buildQueryParams() {
            const params = {
                operator: this.filters.operator || '',
                module: this.filters.module || '',
                name: this.filters.name || '',
                content: this.filters.content || '',
                business_code: this.filters.businessCode || '',
                ip: this.filters.ip || '',
            };
            if (this.filters.timeRange && this.filters.timeRange.length === 2) {
                params.start_time = this.filters.timeRange[0].format('YYYY-MM-DD');
                params.end_time = this.filters.timeRange[1].format('YYYY-MM-DD');
            }
            return params;
        },
        async handleExport() {
            this.exportLoading = true;
            try {
                const params = {
                    ...this.buildQueryParams(),
                    limit: EXPORT_LIMIT,
                    responseType: 'blob',
                };
                const response = await exportOperationLogsWithFullPath(params);
                const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = `操作日志_${dayjs().format('YYYY-MM-DD')}.xlsx`;
                link.click();
                window.URL.revokeObjectURL(link.href);
                this.$message.success('导出成功');
            } catch (error) {
                this.$message.error('导出失败');
            } finally {
                this.exportLoading = false;
            }
        },
        async fetchData() {
            this.loading = true;
            try {
                const params = {
                    ...this.buildQueryParams(),
                    page: this.pagination.current,
                    page_size: this.pagination.pageSize,
                };
                const response = await getOperationLogsWithFullPath(params);

                if (response && response.code === 200) {
                    const logData = response.data || [];
                    const total = response.count || 0;

                    this.allLogData = logData.map(item => ({
                        key: item.id,
                        id: item.id,
                        operator: item.operator || '',
                        module: item.module || '',
                        name: item.name || '',
                        nameDisplay: getOperationDisplay(item.name) || item.name || '',
                        content: item.content || '',
                        contentDisplay: parseOperationContent(item.content, item.name),
                        status: item.status || '成功',
                        time: item.create_time || '',
                        businessCode: item.business_code || '',
                        ip: item.ip || '',
                    }));
                    this.pagination.total = total;
                } else {
                    this.allLogData = [];
                    this.pagination.total = 0;
                }
            } catch (error) {
                this.$message.error('获取日志数据失败');
            } finally {
                this.loading = false;
            }
        },
        handleFilterChange() {
            this.pagination.current = 1;
            this.fetchData();
        },
        handleTableChange(pagination) {
            this.pagination.current = pagination.current;
            this.pagination.pageSize = pagination.pageSize;
            this.fetchData();
        },
        handleReset() {
            this.filters = {
                operator: '',
                module: '',
                name: '',
                content: '',
                timeRange: [],
                businessCode: '',
                ip: '',
            };
            this.pagination.current = 1;
            this.fetchData();
        },
        renderDetailContent(h, record) {
            const labelStyle = 'color: #999; min-width: 90px; display: inline-block;';
            const rowStyle = 'margin-bottom: 12px;';
            const statusColor = record.status === '成功' ? '#52c41a' : (record.status === '失败' ? '#ff4d4f' : '#1890ff');
            return h('div', { style: 'max-height: 500px; overflow-y: auto; padding: 10px;' }, [
                h('div', { style: rowStyle }, [
                    h('span', { style: labelStyle }, '日志编号：'),
                    h('span', { style: 'font-weight: 500;' }, record.id),
                ]),
                h('div', { style: rowStyle }, [
                    h('span', { style: labelStyle }, '操作人：'),
                    h('span', { style: 'font-weight: 500;' }, record.operator),
                ]),
                h('div', { style: rowStyle }, [
                    h('span', { style: labelStyle }, '操作模块：'),
                    h('span', {}, record.module),
                ]),
                h('div', { style: rowStyle }, [
                    h('span', { style: labelStyle }, '操作名称：'),
                    h('span', {}, record.nameDisplay || record.name),
                ]),
                h('div', { style: rowStyle }, [
                    h('span', { style: labelStyle }, '状态：'),
                    h('span', { style: `color: ${statusColor}` }, record.status || '成功'),
                ]),
                h('div', { style: rowStyle }, [
                    h('span', { style: labelStyle + 'vertical-align: top;' }, '操作内容：'),
                    h('div', {
                        style: 'display: inline-block; max-width: 450px; word-break: break-all; white-space: pre-wrap; background: #f5f5f5; padding: 8px; border-radius: 4px; font-size: 13px; line-height: 1.6;',
                    }, record.contentDisplay || record.content || '无'),
                ]),
                h('div', { style: rowStyle }, [
                    h('span', { style: labelStyle + 'vertical-align: top;' }, '原始数据：'),
                    h('div', {
                        style: 'display: inline-block; max-width: 450px; word-break: break-all; white-space: pre-wrap; background: #fafafa; padding: 8px; border-radius: 4px; font-family: monospace; font-size: 11px; color: #666; border: 1px solid #e8e8e8;',
                    }, record.content || '无'),
                ]),
                h('div', { style: rowStyle }, [
                    h('span', { style: labelStyle }, '操作时间：'),
                    h('span', {}, record.time),
                ]),
                h('div', { style: rowStyle }, [
                    h('span', { style: labelStyle }, '业务编号：'),
                    h('span', {}, record.businessCode || '-'),
                ]),
                h('div', {}, [
                    h('span', { style: labelStyle }, 'IP地址：'),
                    h('span', {}, record.ip),
                ]),
            ]);
        },
        showDetails(record) {
            Modal.info({
                title: '操作日志详情',
                content: h => this.renderDetailContent(h, record),
                width: 650,
                icon: null,
                okText: '关闭',
                maskClosable: true,
                keyboard: true,
            });
        },
    },
};
</script>

<style lang="less" scoped>
.export-section {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
}

.operation-log {
    padding: 24px;
    background-color: #fff;

    .filter-section {
        margin-bottom: 24px;
        padding: 16px 24px;
        background-color: #f9f9f9;
        border-radius: 2px;
        border: 1px solid #f0f0f0;
    }

    .filter-row {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .filter-item {
        display: flex;
        align-items: center;
        margin-right: 24px;
        margin-bottom: 8px;

        span {
            margin-right: 8px;
            white-space: nowrap;
            color: rgba(0, 0, 0, 0.85);
            font-size: 14px;
        }

        &.button-group {
            margin-left: auto;
        }
    }

    :deep(.ant-table) {
        .ant-table-thead>tr>th {
            background-color: #f5f7fa;
            color: rgba(0, 0, 0, 0.85);
            font-weight: 500;
        }

        .table-row {
            &:hover {
                background-color: #e6f7ff;
            }
        }
    }

    .detail-btn {
        color: #1890ff;
        padding: 0 4px;
        cursor: pointer;

        &:hover {
            color: #40a9ff;
        }
    }
}
</style>
